import * as THREE from "three";

export default class ShapeUtils {

	static createMaterialWithTexture(url: string, textureRepeat: {x: number, y: number}, anisotropy: number): THREE.MeshBasicMaterial {
		const onTextureLoad = (texture: THREE.Texture) => { 
			texture.userData.status = "loaded"
			texture.dispatchEvent({type: "load"});
		};
		const texture = new THREE.TextureLoader().load(
			url,
			onTextureLoad
		);
		if (texture.userData.status === undefined) {
			texture.userData.status = "loading";
		}

		texture.wrapS = THREE.RepeatWrapping;
		texture.wrapT = THREE.RepeatWrapping;
		texture.repeat.set(textureRepeat.x, textureRepeat.y);
		texture.anisotropy = anisotropy;

		return new THREE.MeshBasicMaterial({
			map: texture,
			color: 0xffffff,
		});
	}

	public static svgStringToSrcData(svgString: string) {
		// Remove any characters outside the Latin1 range
		const decoded = unescape(encodeURIComponent(svgString));

		// Now we can use btoa to convert the svg to base64
		const base64 = btoa(decoded);

		return `data:image/svg+xml;base64,${base64}`;
	}

	static hideMeshWhileTextureOnMaterialLoads(mesh: THREE.Mesh) {
		const material = mesh.material as THREE.MeshBasicMaterial;
		if (
			material.map !== undefined
			&& material.map.userData !== undefined
			&& material.map.userData.status === "loading"
		) {
			mesh.visible = false;
			material.map.addEventListener("load", () => {
				requestAnimationFrame(() => {
					mesh.visible = true;
				});
			});
		}
	}

	static createSphere(radius: number, material: THREE.Material): THREE.Mesh {
		const mesh = new THREE.Mesh(
			new THREE.SphereGeometry(radius, 42, 42, 0, Math.PI * 2, 0, Math.PI * 2),
			material
		);
		ShapeUtils.hideMeshWhileTextureOnMaterialLoads(mesh);
		return mesh;
	}

	static createTorus(radius: number, radiusOfTube: number, material: THREE.Material): THREE.Mesh {
		const mesh = new THREE.Mesh(
			new THREE.TorusGeometry(radius, radiusOfTube, 20, 50),
			material
		);
		ShapeUtils.hideMeshWhileTextureOnMaterialLoads(mesh);
		return mesh;
	}


	static scaleToFillView(mesh: THREE.Mesh, scaleZ: number, camera: THREE.PerspectiveCamera, cameraPositionOverride?: THREE.Vector3) {
		mesh.scale.setScalar(1);

		const box = new THREE.Box3().setFromObject(mesh);
		const sizeInfo = ShapeUtils.getSizeInfo(box, camera, cameraPositionOverride);
		const scalarWidth = sizeInfo.maxVisibleWidth / sizeInfo.width;
		const scalarHeight = sizeInfo.maxVisibleHeight / sizeInfo.height;

		mesh.scale.set(
			scalarWidth,
			scalarHeight,
			scaleZ // Depth of room
		);

		return {scalarWidth, scalarHeight};
	}

	static getMaxSizes(position: THREE.Vector3, camera: THREE.PerspectiveCamera, cameraPositionOverride?: THREE.Vector3):
		{width: number, height: number}
	{
		const distance = position.distanceTo(
			cameraPositionOverride !== undefined
			? cameraPositionOverride
			: camera.position
		);

		const verticleFOV = THREE.MathUtils.degToRad(camera.getEffectiveFOV());
		const height = 2 * Math.tan(verticleFOV / 2) * distance;
		const width = height * camera.aspect;

		return {
			width,
			height
		}
	}

	static getSizeInfo(box: THREE.Box3, camera: THREE.PerspectiveCamera, cameraPositionOverride?: THREE.Vector3):
		{width: number, height: number, maxVisibleWidth: number, maxVisibleHeight: number}
	{
		const boxSize = box.getSize(new THREE.Vector3());
		const boxCenter = box.getCenter(new THREE.Vector3());
		const maxSize = ShapeUtils.getMaxSizes(boxCenter, camera, cameraPositionOverride);

		return {
			width: boxSize.x,
			height: boxSize.y,
			maxVisibleWidth: maxSize.width,
			maxVisibleHeight: maxSize.height,
		}
	}
}